import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { providersAPI } from '../api/providers';
import {
  getFavorites,
  removeFavorite,
  FAVORITES_EVENT,
} from '../utils/favorites';
import Spinner from '../components/common/Spinner';
import {
  HeartIcon as HeartOutline,
  MapPinIcon,
  CheckBadgeIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid, StarIcon as StarSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const Favorites = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const ids = getFavorites(user.id);
      if (ids.length === 0) {
        setProviders([]);
        return;
      }
      const results = await Promise.all(
        ids.map((id) => providersAPI.getProvider(id).catch(() => null))
      );
      const staleIds = [];
      const alive = [];
      results.forEach((p, i) => {
        if (p) alive.push(p);
        else staleIds.push(ids[i]);
      });
      // Cleanup: if a favorited provider no longer exists, drop it silently.
      staleIds.forEach((id) => removeFavorite(user.id, id));
      setProviders(alive);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = (e) => {
      if (!e.detail || e.detail.userId !== user?.id) return;
      load();
    };
    window.addEventListener(FAVORITES_EVENT, handler);
    return () => window.removeEventListener(FAVORITES_EVENT, handler);
  }, [user?.id, load]);

  const handleRemove = (id, name) => {
    removeFavorite(user.id, id);
    toast.success(`Removed ${name || 'provider'} from favorites`);
  };

  const filtered = search.trim()
    ? providers.filter((p) => {
        const q = search.toLowerCase();
        return (
          (p.businessName || '').toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q) ||
          (p.location || '').toLowerCase().includes(q)
        );
      })
    : providers;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div style={{ padding: '32px 32px 32px 24px' }}>

        {/* Header */}
        <div className="mb-6 text-center">
          <h1
            style={{
              fontSize: '26px',
              fontWeight: 700,
              margin: 0,
              color: 'var(--gray-12)',
              textAlign: 'center',
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Favorites
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '4px 0 0', textAlign: 'center' }}>
            {providers.length === 0
              ? 'Providers you save from Search will appear here.'
              : `${providers.length} saved provider${providers.length === 1 ? '' : 's'}`}
          </p>
        </div>

        {/* Search bar (only useful with >0 favorites) */}
        {providers.length > 0 && (
          <div className="mb-5">
            <div
              className="flex items-center gap-2 px-4 h-11 rounded-full border bg-white"
              style={{ borderColor: 'var(--gray-5)' }}
            >
              <MagnifyingGlassIcon className="w-4 h-4 shrink-0" style={{ color: 'var(--gray-9)' }} />
              <input
                type="text"
                placeholder="Search your favorites..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent text-sm focus:outline-none"
                style={{ color: 'var(--gray-12)' }}
              />
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="large" />
          </div>
        ) : providers.length === 0 ? (
          <div
            className="text-center py-20 rounded-2xl border"
            style={{ borderColor: 'var(--gray-4)', backgroundColor: '#fff' }}
          >
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ backgroundColor: 'var(--orange-3)' }}
            >
              <HeartOutline style={{ width: '28px', height: '28px', color: 'var(--orange-9)' }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--gray-12)' }}>
              No favorites yet
            </h3>
            <p className="text-sm mb-5" style={{ color: 'var(--gray-10)' }}>
              Tap the heart icon on any provider to save them here.
            </p>
            <Link
              to="/search"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold text-white"
              style={{ backgroundColor: 'var(--orange-9)', textDecoration: 'none' }}
            >
              Browse Providers
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="text-center py-12 rounded-2xl border"
            style={{ borderColor: 'var(--gray-4)', backgroundColor: '#fff' }}
          >
            <p style={{ fontSize: '14px', color: 'var(--gray-10)' }}>
              No favorites match "{search}".
            </p>
          </div>
        ) : (
          <ul className="divide-y rounded-2xl border" style={{ borderColor: 'var(--gray-4)', backgroundColor: '#fff' }}>
            {filtered.map((provider) => (
              <li key={provider.id} style={{ borderColor: 'var(--gray-4)' }}>
                <div className="flex items-start gap-4 py-4 px-4">
                  <Link
                    to={`/providers/${provider.id}`}
                    className="shrink-0"
                    style={{ textDecoration: 'none' }}
                  >
                    {provider.photoUrl ? (
                      <img
                        src={provider.photoUrl}
                        alt={provider.businessName}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center text-base font-bold"
                        style={{ backgroundColor: 'var(--orange-3)', color: 'var(--orange-9)' }}
                      >
                        {provider.businessName?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                  </Link>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-1">
                      <Link
                        to={`/providers/${provider.id}`}
                        className="flex items-center gap-1.5 min-w-0"
                        style={{ textDecoration: 'none' }}
                      >
                        <h3
                          className="text-sm sm:text-base font-semibold truncate"
                          style={{ color: 'var(--gray-12)' }}
                        >
                          {provider.businessName}
                        </h3>
                        {provider.isApproved && (
                          <CheckBadgeIcon className="w-4 h-4 shrink-0" style={{ color: 'var(--orange-9)' }} />
                        )}
                      </Link>
                      <div className="flex items-center gap-1 shrink-0">
                        <StarSolid style={{ width: '13px', height: '13px', color: '#facc15' }} />
                        <span className="text-xs font-semibold" style={{ color: 'var(--gray-12)' }}>
                          {(provider.rating || 0).toFixed(1)}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--gray-9)' }}>
                          ({provider.totalReviews || 0})
                        </span>
                      </div>
                    </div>

                    {provider.description && (
                      <p
                        className="text-xs sm:text-[13px] leading-relaxed mb-2"
                        style={{
                          color: 'var(--gray-10)',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {provider.description}
                      </p>
                    )}

                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
                      <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--gray-10)' }}>
                        <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{provider.location || 'Location unavailable'}</span>
                      </span>
                      {provider.offersMobile && (
                        <span
                          className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: 'var(--orange-2)', color: 'var(--orange-10)' }}
                        >
                          Mobile
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleRemove(provider.id, provider.businessName)}
                      title="Remove from favorites"
                      aria-label="Remove from favorites"
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                      style={{
                        border: '1px solid var(--orange-6)',
                        backgroundColor: 'var(--orange-2)',
                        color: 'var(--orange-9)',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--orange-3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--orange-2)';
                      }}
                    >
                      <HeartSolid style={{ width: '18px', height: '18px' }} />
                    </button>
                    <Link
                      to={`/providers/${provider.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold"
                      style={{
                        color: 'var(--orange-9)',
                        textDecoration: 'none',
                        padding: '4px 10px',
                      }}
                    >
                      View
                      <ArrowRightIcon className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Favorites;
